import React, { useState } from 'react';
import { areaSeoContent } from '../data/seoContent';
import { SchoolListing } from '../types/api';
import { ChevronDownIcon, BookOpenIcon, SearchIcon, GraduationCapIcon } from 'lucide-react';
interface SeoContentSectionProps {
  areaStudyId: number;
  topSchool?: SchoolListing;
}
export function SeoContentSection({
  areaStudyId,
  topSchool
}: SeoContentSectionProps) {
  const seoData = areaSeoContent[areaStudyId];
  if (!seoData) return null;
  return <section className="w-full bg-gray-50 border-t border-gray-200 py-12 md:py-16" data-id="element-697" data-mp="br84tjb3brue">
      <div className="max-w-4xl mx-auto px-4" data-id="element-698" data-mp="gq1c2tzsamm4">
        {/* SEO Title */}
        <div className="flex items-center gap-3 mb-3" data-id="element-699" data-mp="llgmfbyl20ds">
          <div className="bg-[#1a2332] p-2 rounded-lg text-white" data-id="element-700" data-mp="qluz57b4pyi5">
            <GraduationCapIcon className="w-5 h-5" data-id="element-701" data-mp="qk1olgxeau4v" />
          </div>
          <span className="text-xs font-semibold text-[#3b82f6] uppercase tracking-wider" data-id="element-702" data-mp="1x1f2hzs6hmoi">
            Program Guide
          </span>
        </div>

        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4" data-id="element-703" data-mp="16e4fo6pd2q95">
          {seoData.pageTitle}
        </h2>

        <p className="text-base text-gray-600 leading-relaxed mb-10" data-id="element-704" data-mp="11ei5k64txq4k">
          {seoData.intro}
        </p>

        {/* Content Sections */}
        <div className="space-y-8 mb-10" data-id="element-705" data-mp="xbv7volh9azg">
          {seoData.sections.map((section, idx) => <div key={idx} data-id="element-706" data-mp="sdjx9bncywg6" data-mpi={idx}>
              <h3 className="text-lg font-bold text-gray-900 mb-2 flex items-start gap-2" data-id="element-707" data-mp="sgfwd3axzr67">
                <SearchIcon className="w-5 h-5 text-[#3b82f6] flex-shrink-0 mt-0.5" data-id="element-708" data-mp="ngjsr565q1r6" />
                {section.heading}
              </h3>
              <p className="text-sm text-gray-700 leading-relaxed pl-7" data-id="element-709" data-mp="dp5x58hbtm3b">
                {section.body}
              </p>
            </div>)}
        </div>

        {/* Inline School Ad */}
        {topSchool && <a href={topSchool.clickURL} target="_blank" rel="noopener noreferrer" className="block bg-gradient-to-r from-gray-50 to-blue-50/80 rounded-2xl p-6 md:p-8 border-2 border-blue-200 mb-10 hover:shadow-lg transition-all group" data-id="element-710" data-mp="4ohh688dhbns">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-5" data-id="element-711" data-mp="3m6n2pxe5z0yg">
              <div className="flex flex-col sm:flex-row items-center gap-4 flex-1" data-id="element-712" data-mp="3r7ki627shgw9">
                <div className="w-28 h-18 flex items-center justify-center bg-white rounded-xl border border-gray-200 p-3 flex-shrink-0" data-id="element-713" data-mp="3ca1xbay8fnu3">
                  <img src={topSchool.imageURL} alt={`${topSchool.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-714" data-mp="3h8lktfsh57ok" />
                </div>
                <div className="text-center sm:text-left" data-id="element-715" data-mp="ds66lo1esh7r">
                  <p className="text-base font-bold text-gray-900 mb-1" data-id="element-716" data-mp="dtpn9fqz8ua2">
                    {topSchool.mBrandName}
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-717" data-mp="41bjkhgc9dl9">
                    {topSchool.headContent || 'Accredited Degrees'}
                  </p>
                </div>
              </div>
              <div className="bg-[#6366f1] group-hover:bg-[#4f46e5] text-white font-bold py-3 px-8 rounded-xl transition-colors shadow-sm whitespace-nowrap text-sm" data-id="element-718" data-mp="whuks5h1jj0f">
                Visit Site
              </div>
            </div>
          </a>}

        {/* FAQ Section */}
        {seoData.faq.length > 0 && <div className="bg-white rounded-xl border border-gray-200 p-6 md:p-8" data-id="element-719" data-mp="2qy2z75itc648">
            <div className="flex items-center gap-2 mb-5" data-id="element-720" data-mp="2r05rjw7fubc4">
              <BookOpenIcon className="w-5 h-5 text-[#e8594f]" data-id="element-721" data-mp="2m0krtjkg1yyc" />
              <h3 className="text-lg font-bold text-gray-900" data-id="element-722" data-mp="1y95jna5zknf6">
                Frequently Asked Questions
              </h3>
            </div>

            <div className="space-y-1" data-id="element-723" data-mp="1tc5fj4sp5uqp">
              {seoData.faq.map((item, idx) => <FaqItem key={idx} question={item.question} answer={item.answer} defaultOpen={idx === 0} data-id="element-724" data-mp="27ber1r5yk0oc" data-mpi={idx} />)}
            </div>
          </div>}
      </div>
    </section>;
}
function FaqItem({
  question,
  answer,
  defaultOpen
}: {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen || false);
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-725" data-mp="dsplc3sh85nw">
      <button type="button" onClick={() => setIsOpen(!isOpen)} className="w-full flex items-center justify-between py-3 text-left hover:text-[#3b82f6] transition-colors" data-id="element-726" data-mp="duai8029k0ox">
        <span className="text-sm font-semibold text-gray-900 pr-4" data-id="element-727" data-mp="msclynddt7fw">
          {question}
        </span>
        <ChevronDownIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-728" data-mp="rtieak34qsow" />
      </button>
      <div className={`overflow-hidden transition-all duration-200 ${isOpen ? 'max-h-40 opacity-100 pb-3' : 'max-h-0 opacity-0'}`} data-id="element-729" data-mp="10t5kdj6t0moa">
        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-730" data-mp="15jlxf26pq6a4">{answer}</p>
      </div>
    </div>;
}